import React, {
  ReactElement,
  useContext, useEffect,
  useMemo,
  useState
} from 'react';
import {
  EventData, Plan,
  PLAN_UPDATED,
  ProjectType, SetupAreaIdContext,
  useEventsSubscriber,
  usePlansService
} from 'nekst-api';
import PlanTasksList from '../tasks/List';
import { LoadingAnimation, MassUpdate } from 'ui-builder';
import { PlanAuthorizationScope, PlanPermission } from 'authorization-scope';
import { PlanContext } from '../tasks/List/PlanContext';

export interface BaseViewPlanProps {
  id: number,
  breadcrumbs?: ReactElement;
  setupAreaId?: number
  setupAreaVersion?: string;
}

export interface ViewPlanProps {
  id: number;
  marketId?: number,
  breadcrumbs?: ReactElement;
}

export interface ViewPlanAsAdminProps extends ViewPlanProps {
  setupAreaId: number;
  setupAreaVersion: string;
}

export type PlanContextValue = {
  projectType?: ProjectType
  detailsFieldsSetId?: number
}

const ProjectTypeAndSetIdReactContext = React.createContext<PlanContextValue | undefined>(undefined);

export function ProjectTypeContext(props: {
  projectType: ProjectType,
  applyFieldsHierarchy?: boolean,
  detailsFieldsSetId?: number,
  children: ListOrSingle<ReactElement>,
}) {
  const contextValue = useMemo(() => {
    return {
      projectType: props.projectType,
      detailsFieldsSetId: props.detailsFieldsSetId,
      applyFieldsHierarchy: props.applyFieldsHierarchy
    };
  }, [props.projectType, props.detailsFieldsSetId, props.applyFieldsHierarchy]);

  return (
    <ProjectTypeAndSetIdReactContext.Provider value={contextValue}>
      {props.children}
    </ProjectTypeAndSetIdReactContext.Provider>
  );
}

export function useProjectTypeAndSetIdContextRequired() {
  const context = useContext(ProjectTypeAndSetIdReactContext);

  if (!context) {
    throw new Error('usePlanContextRequired must be used within a PlanContext');
  }

  return context;
}

function BaseViewPlan(props: BaseViewPlanProps) {
  const plansService = usePlansService();

  const [plan, setPlan] = useState<Plan>();

  async function updatePlanData() {
    const planData = await plansService.getById(props.id);
    setPlan(planData);
  }

  useEventsSubscriber(
    'ViewPlan',
    {
      [PLAN_UPDATED]: (eventData: EventData) => {
        if (eventData.data.id === props.id) {
          setPlan(eventData.data);
        }
      }
    }
  );

  useEffect(() => {
    updatePlanData();
  }, [props.id]);

  if (plan) {
    return (
      <SetupAreaIdContext data={{
        setupAreaId: plan._setupArea?.id ?? 0
      }}>
        <MassUpdate>
          <ProjectTypeContext
            projectType={plan.projectType}
            detailsFieldsSetId={plan.detailsFieldsSetId}
            applyFieldsHierarchy={plan._setupArea?.applyFieldsHierarchy}
          >
            <PlanAuthorizationScope
              id={props.id}
              permissionToCheck={PlanPermission.READ}
              disableGlobalPlansManagement={!props.setupAreaId && !props.setupAreaVersion}
            >
              <PlanContext id={props.id}>
                {props.breadcrumbs}
                <PlanTasksList
                  plan={plan}
                  detailsFieldsSetName={plan._detailsFieldsSet?.name}
                  setupAreaId={props.setupAreaId}
                  setupAreaVersion={props.setupAreaVersion}
                />
              </PlanContext>
            </PlanAuthorizationScope>
          </ProjectTypeContext>

        </MassUpdate>
      </SetupAreaIdContext>

    );
  } else {
    return (
      <LoadingAnimation />
    );
  }
}

export default function ViewPlan(props: ViewPlanProps) {
  return (
    <BaseViewPlan id={props.id} breadcrumbs={props.breadcrumbs} />
  )
}

export function ViewPlanAsAdmin(props: ViewPlanAsAdminProps) {
  return (
    <BaseViewPlan
      id={props.id}
      breadcrumbs={props.breadcrumbs}
      setupAreaId={props.setupAreaId}
      setupAreaVersion={props.setupAreaVersion}
    />
  );
}
